// app/(lib)/services/privacyPolicyService.ts
import { apiClient } from "../../app/api/client";
import { handleAPIError } from "../utils/apiErrorHandler";

export interface PrivacyPolicySection {
  heading?: string;
  effectiveDate?: string;
  content?: string;
}

export interface PrivacyPolicyContent {
  privacyPolicy?: PrivacyPolicySection;
}

export interface PrivacyPolicyPageData {
  id: string;
  franchisee_id: string | null;
  is_global: boolean;
  title: string;
  slug: string;
  template_id: string;
  content: PrivacyPolicyContent;
  translated_content?: PrivacyPolicyContent;
  status: string;
  seo_title?: string | null;
  seo_description?: string | null;
  version?: number;
  effective_language?: string;
}

export interface PrivacyPolicyDataWithTranslations {
  default: PrivacyPolicyContent;
  translated?: PrivacyPolicyContent;
  version: number;
  language_code: string;
}

const DEFAULTS: PrivacyPolicySection = {
  heading: "Privacy Policy",
  effectiveDate: "",
  content: "Privacy policy content is not available at this time. Please contact us for more information.",
};

export class PrivacyPolicyService {
  private readonly endpoint = "pages/privacy-policy";

  async fetchByDomain(
    domain: string,
    franchiseeId?: string | null,
    language?: string | null,
  ): Promise<PrivacyPolicyDataWithTranslations> {
    const startTime = Date.now();

    try {
      const params: Record<string, string> = {};
      if (franchiseeId) params.franchiseeId = franchiseeId;
      if (language && language !== "default") params.lang = language;

      const response = await apiClient.get<{ status: boolean; data: PrivacyPolicyPageData }>(
        this.endpoint,
        {
          headers: { "X-Domain": domain },
          params: Object.keys(params).length > 0 ? params : undefined,
        },
      );

      if (!response.data?.status || !response.data?.data?.content) {
        return this.getDefaultData();
      }

      const elapsed = Date.now() - startTime;

      return {
        default: response.data.data.content,
        translated: response.data.data.translated_content || undefined,
        version: response.data.data.version ?? 1,
        language_code: response.data.data.effective_language ?? 'en',
      };
    } catch (error) {
      return handleAPIError(error, domain, startTime, this.getDefaultData.bind(this));
    }
  }

  private async getDefaultData(): Promise<PrivacyPolicyDataWithTranslations> {
    return {
      default: { privacyPolicy: DEFAULTS },
      translated: undefined,
      version: 1,
      language_code: 'en',
    };
  }
}

export const privacyPolicyService = new PrivacyPolicyService();
